import type { GQL } from '@aline/graphql';
import type { BaseRange } from 'slate';

import type { UUID } from '../../types';
import type {
  AlineContentBlock,
  DeleteEdit,
  DeleteParagraphEdit,
  EditEntry,
  InsertBookmarkEdit,
  InsertBreakEdit,
  InsertCheckboxEdit,
  InsertDrawingEdit,
  InsertEdit,
  InsertInstructionFieldEdit,
  InsertParagraphEdit,
  InsertTabEdit,
  ParagraphChildElement,
  ParagraphListProps,
  RemoveBreakEdit,
  RemoveCheckboxEdit,
  RemoveDrawingEdit,
  RemoveInstructionFieldEdit,
  RemoveTabEdit,
  SuggestedDocEdit,
  SuggestedFootersEdit,
  SuggestedHeadersEdit,
  SuggestedLinkEdit,
  SuggestedParagraphEdit,
  TextFormatEdit,
  ToggleCheckboxEdit,
} from '../internal';
import type { CursorData } from '../plugins/cursors/types';

export type SelectionAffinity = 'forward' | 'backward';

export interface AlineSelection extends BaseRange {
  affinity?: SelectionAffinity;
  /**
   * The grid column under the pointer when the anchor was placed by the mouse. Refines which column
   * of a merged anchor cell the selection rectangle is anchored to; absent for keyboard/programmatic
   * selections. See Queries.getGridSelectionInfo.
   */
  gridSelectionAnchorColumn?: number | null;
  /** The grid column under the pointer when the focus was placed by the mouse. */
  gridSelectionFocusColumn?: number | null;
  /**
   * List props of the paragraph the selection is in. `null` clears the marker —
   * the selection is ephemeral editor state, not a stored node, so `null` is fine
   * here (unlike `ParagraphElement.listProps`).
   */
  listMarker?: ParagraphListProps | null;
  moveVertWidth?: number | null;
}

export type RemoteSelection = BaseRange &
  CursorData & {
    /**
     * Set on the synthetic per-cell entries a remote grid selection is expanded into, so the
     * renderer highlights the cell but doesn't also draw a caret/name for it.
     */
    gridCellHighlight?: boolean;
    /**
     * Render-only marker set by expandRemoteGridSelections on a grid selection's base (caret/name)
     * entry so text.ts suppresses its linear highlight. Derived from geometry on receipt of a
     * collaborator's cursor, never transmitted.
     */
    isGridSelection?: boolean;
  };

export interface SelectionIntersection extends BaseRange {
  /**
   * Whether the original selection is expanded, regardless of whether this intersection is
   * expanded.
   */
  isExpanded: boolean;
  /**
   * Whether this intersection contains the focus of the original selection.
   */
  isOriginalFocus: boolean;
}

export type AlineSelectionIntersection = AlineSelection & SelectionIntersection;
export type RemoteSelectionIntersection = RemoteSelection & SelectionIntersection;

export function isSelectionIntersection(range: BaseRange): range is SelectionIntersection {
  return 'isExpanded' in range && 'isOriginalFocus' in range;
}

export enum SelEventSource {
  KEYBOARD = 'KEYBOARD',
  MOUSE = 'MOUSE',
  PROGRAMMATIC = 'PROGRAMMATIC',
}

export interface InsertEditEntry extends EditEntry {
  edit: InsertEdit;
}

export interface DeleteEditEntry extends EditEntry {
  edit: DeleteEdit;
}

export interface InsertParagraphEditEntry extends EditEntry {
  edit: InsertParagraphEdit;
}

export interface DeleteParagraphEditEntry extends EditEntry {
  edit: DeleteParagraphEdit;
}

export interface InsertBreakEditEntry extends EditEntry {
  edit: InsertBreakEdit;
}

export interface InsertBookmarkEditEntry extends EditEntry {
  edit: InsertBookmarkEdit;
}

export interface InsertCheckboxEditEntry extends EditEntry {
  edit: InsertCheckboxEdit;
}

export interface InsertTabEditEntry extends EditEntry {
  edit: InsertTabEdit;
}

export interface InsertDrawingEditEntry extends EditEntry {
  edit: InsertDrawingEdit;
}

export interface RemoveDrawingEditEntry extends EditEntry {
  edit: RemoveDrawingEdit;
}

export interface RemoveBreakEditEntry extends EditEntry {
  edit: RemoveBreakEdit;
}

export interface RemoveCheckboxEditEntry extends EditEntry {
  edit: RemoveCheckboxEdit;
}

export interface ToggleCheckboxEditEntry extends EditEntry {
  edit: ToggleCheckboxEdit;
}

export interface RemoveTabEditEntry extends EditEntry {
  edit: RemoveTabEdit;
}

export interface LinkEditEntry extends EditEntry {
  edit: SuggestedLinkEdit;
}

export interface ParagraphFormatEditEntry extends EditEntry {
  edit: SuggestedParagraphEdit;
}

export interface DocEditEntry extends EditEntry {
  edit: SuggestedDocEdit;
}

export interface FootersPartEditEntry extends EditEntry {
  edit: SuggestedFootersEdit;
}

export interface HeadersPartEditEntry extends EditEntry {
  edit: SuggestedHeadersEdit;
}

export interface TextFormatEditEntry extends EditEntry {
  edit: TextFormatEdit;
}

export interface InsertInstructionFieldEditEntry extends EditEntry {
  edit: InsertInstructionFieldEdit;
}

export interface RemoveInstructionFieldEditEntry extends EditEntry {
  edit: RemoveInstructionFieldEdit;
}

export type DraggedBorder = 'left' | 'right';
export type IndentationKey = 'indentationFirstLine' | 'indentationLeft' | 'indentationRight';

export type ParagraphFormatKey =
  | 'alignment'
  | 'borders'
  | 'indentationFirstLine'
  | 'indentationLeft'
  | 'indentationRight'
  | 'lineSpacing'
  | 'listProps'
  | 'shade'
  | 'spacingBefore'
  | 'spacingAfter';

export type ContentNodes = AlineContentBlock[] | ParagraphChildElement[];

export type ProcessTemplateAnswerOption = Pick<GQL.AnswerOption, '__typename' | 'optionText'> & {
  choiceRefId: UUID | null;
};

export type ProcessTemplateCheckboxOption = {
  /** The option's `choiceRefId`, which is what checkbox nodes are keyed by. */
  id: UUID;
  optionText: string;
  /**
   * The `AnswerOption` row id. Documents written before checkbox nodes were keyed by
   * `choiceRefId` hold it instead, so it is used to find those nodes and rename them.
   */
  legacyId?: UUID;
};

export type ProcessTemplateInitialValue =
  | { type: 'field-value'; value: string }
  | {
      answerOption: ProcessTemplateAnswerOption & { choiceRefId: UUID };
      type: 'answer-option';
    }
  /**
   * Checkbox fields hold a set of options rather than a single value, so they carry the
   * authoritative option list plus the options to check. `selectedAnswerIds` distinguishes
   * "check exactly these" (including `[]` for none) from `undefined`, which leaves the
   * template's existing checked state alone.
   */
  | {
      answerOptions: ProcessTemplateCheckboxOption[];
      type: 'checkbox';
      selectedAnswerIds?: UUID[];
    };

export interface ProcessTemplateField extends Pick<
  GQL.DocumentVariable,
  'label' | 'nodeId' | '__typename' | 'variant'
> {
  defaultAnswer: ProcessTemplateAnswerOption | null;
  initialValue?: ProcessTemplateInitialValue | null;
}

/**
 * A map of old drawing ids to new drawing ids.
 */
export type DrawingFileMap = Record<UUID, UUID>;
